import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Cookie } from "lucide-react";
import logo from "@/assets/codelumen-logo-3.webp";
import FourBarSeparator from "./FourBarSeparator";
import { useCookieConsent } from "./CookieConsent";

const SiteFooter = () => {
  const { t } = useTranslation();
  const { openBanner } = useCookieConsent();
  const footerLinks = [
    {
      heading: t("footer.industries"),
      links: [
        { label: t("nav.warehouse"), path: "/solutions/warehouse-distribution" },
        { label: t("nav.manufacturing"), path: "/industries/manufacturing" },
        { label: t("nav.dataCenter"), path: "/industries/data-center" },
        { label: t("footer.industrialParks"), path: "/solutions" },
        { label: t("footer.parkingStructures"), path: "/solutions" },
      ],
    },
    {
      heading: t("footer.resources"),
      links: [
        { label: t("footer.performanceReview"), path: "/contact" },
        { label: t("footer.savingsCalculator"), path: "/calculator" },
        { label: t("footer.industrialStrategy"), path: "/solutions" },
        { label: t("footer.packageReview"), path: "/#package-review" },
        { label: t("nav.performanceLab"), path: "/performance-lab" },
      ],
    },
    {
      heading: t("footer.company"),
      links: [
        { label: t("footer.about"), path: "/about" },
        { label: t("footer.contact"), path: "/contact" },
      ],
    },
    {
      heading: t("footer.legal"),
      links: [
        { label: t("footer.terms"), path: "/terms" },
        { label: t("footer.privacy"), path: "/privacy" },
      ],
    },
  ];

  return (
    <footer className="pt-10 pb-6 border-t border-border" style={{ contain: "layout" }}>
      <FourBarSeparator />
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8 md:gap-10 mb-8">
          <div className="col-span-2">
            <img src={logo} alt="CodeLumen Performance" className="w-48 h-auto mb-4" width={192} height={56} />
            <p className="text-[11px] text-muted-foreground tracking-[0.2em] uppercase mb-1">
              CodeLumen LLC · {t("footer.tagline")}
            </p>
            <p className="text-[10px] text-engineering/80 tracking-[0.15em] uppercase mb-2">
              {t("footer.taglineSecondary")}
            </p>
            <p className="text-[11px] text-muted-foreground/70 tracking-[0.15em] uppercase mb-5">
              {t("footer.engineeredInUSA")}
            </p>
            <div className="flex flex-col gap-1.5 text-sm text-muted-foreground">
              <a href="mailto:info@codelumen.com" className="hover:text-foreground transition-colors">
                info@codelumen.com
              </a>
              <span>El Paso, Texas</span>
            </div>
          </div>

          {footerLinks.map((group) => (
            <div key={group.heading}>
              <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-foreground/60 mb-4">
                {group.heading}
              </p>
              <div className="flex flex-col gap-2.5">
                {group.links.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="pt-6 border-t border-border/50 flex flex-col md:flex-row justify-between items-center gap-3">
          <div className="flex flex-col md:flex-row items-center gap-2">
            <p className="text-[11px] text-muted-foreground">
              © {new Date().getFullYear()} CodeLumen. All rights reserved.
            </p>
            <span className="hidden md:inline text-muted-foreground/40">·</span>
            <p className="text-[10px] font-semibold tracking-widest uppercase text-engineering/80">
              {t("footer.engineeredInUSA")}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={openBanner}
              className="flex items-center gap-1.5 text-[10px] text-muted-foreground/70 hover:text-foreground transition-colors uppercase tracking-widest"
            >
              <Cookie className="h-3 w-3" />
              Cookie Preferences
            </button>
            <p className="text-[9px] text-muted-foreground/70 max-w-lg text-center md:text-right leading-relaxed">
              {t("footer.dataSources")}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
